import {
    AlertColor,
    Button,
    CircularProgress,
    MenuItem,
    Modal,
    TextField,
    Theme,
    Typography,
    useMediaQuery,
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { globalTheme } from "../../../common/theme";
import createStyles from "@mui/styles/createStyles";
import axios from "axios";
import React, { useImperativeHandle } from "react";
import { SnackBarMessage, SnackBarMessageProps } from "../../component/SnackBarMessage";
import Title from "../../component/Title";
import { DropDownComboOption } from "../../component/GenericFilter";
import { Bank } from "./BankAccessories";
import { getDefaultRegion, renewToken } from "../../../services/AuthService";
import { OpenMode } from "../../../common/Enums";
import { GeneralDialog } from "../../component/GeneralDialog";
import { CommonProps } from "../../component/MainContents";

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
    })
);

export interface BankSingleHandle {
    Initialise: (mode: OpenMode, bank: Bank) => void;
}

export const BankSingle = React.forwardRef(
    (props: CommonProps, ref: React.Ref<BankSingleHandle>) => {
        const style = { ...useStyles(), ...globalTheme() };

        const tinyScreen = useMediaQuery('(max-width:1366px)');
        const smallScreen = useMediaQuery('(max-width:1920px)');
        const normalScreen = useMediaQuery('(max-width:2560px)');
    
        const [open, setOpen] = React.useState(false);
        const [openMode, setOpenMode] = React.useState<OpenMode>(OpenMode.NEW);
        const [loading, setLoading] = React.useState<boolean>(false);
        const [hasError, setHasError] = React.useState(false);
        const [regionsWithBlank, setRegionsWithBlank] = React.useState<DropDownComboOption[]>([]);
        const [bank, setBank] = React.useState<Bank>();
        const [originalBank, setOriginalBank] = React.useState<Bank>();
        const [saved, setSaved] = React.useState<boolean>(false);

        //Dialogs and snacks
        const hasChanges = React.useRef<boolean>(false);
        const [dialogOpen, setDialogOpen] = React.useState<boolean>(false);
        const [snackSettings, setSnackSettings] =
        React.useState<SnackBarMessageProps>({
            open: false,
            message: "",
            subMessage: "",
            severity: undefined,
            onClose: () => setSnackSettings({ open: false, message: "", subMessage: "", severity: undefined, onClose: () => {}})
        });

        function showSnackBar(message: string, subMessage: string, colour?: AlertColor) {
            setSnackSettings({
                open: true,
                message: message,
                subMessage: subMessage,
                severity: colour,
                onClose: () => setSnackSettings({ open: false, message: "", subMessage: "", severity: undefined, onClose: () => {}})
            });
        }

        useImperativeHandle(ref, () => ({
            Initialise(mode: OpenMode, bank: Bank) {
                setOpen(true);
                setSaved(false);
                renewToken();

                let tempRegions = [...props.regions];
                tempRegions.unshift({name: "", value: "-1"});
                setRegionsWithBlank(tempRegions);

                hasChanges.current = false;
                setOpenMode(mode);

                if (bank)
                {
                    setBank(bank);
                    setOriginalBank(bank);
                }
            },
        }));

        function closeModal() {
            setDialogOpen(false);
            setOpen(false);
            props.onClose(saved, originalBank);
        }

        function closeModalCheck(keepOpen: boolean) {
            if (hasChanges.current) {
              setDialogOpen(true);
            } else {
              setDialogOpen(false);
              setOpen(keepOpen);
              props.onClose(saved, originalBank);
            }
        }

        function OnBankChanged(
            propertyName: string,
            propertyValue: any,
            validationRegex?: RegExp
        ) {
            let proceed = true;
        
            if (validationRegex !== undefined) {
                proceed = validationRegex.test(propertyValue);
            }
        
            if (proceed) {
                let temp: any = { ...bank };
        
                if (temp[propertyName] != propertyValue)
                {
                    hasChanges.current = true;
                    temp[propertyName] = propertyValue ?? null;
                    setBank(temp);
                }
            }
        }
          
        function SaveBank(close: boolean) {
            if (hasChanges.current)
            {
                if (openMode === OpenMode.EDIT)
                {
                    axios.put<Bank>('Api/V1/Bank/', bank).then(
                        (result) => {
                            showSnackBar("Bank saved successfully.", "", "success");
                            hasChanges.current = false;
                            setSaved(true);
                            setBank(result.data);
                            setOriginalBank(result.data);
                            setOpen(!close);
                            setDialogOpen(false);
                            if (close)
                                props.onClose(true, result.data);
                        },
                        (error) => {
                        showSnackBar(error.toString(), error?.response?.data?.errors[0], "error");
                        }
                    );
                } 
                else
                {
                    axios.post<Bank>('Api/V1/Bank/', bank).then(
                        (result) => {
                            showSnackBar("Bank created successfully.", "", "success");
                            hasChanges.current = false;
                            setSaved(true);
                            setBank(result.data);
                            setOriginalBank(result.data);
                            setOpenMode(OpenMode.EDIT);
                            setOpen(!close);
                            setDialogOpen(false);
                            if (close)
                                props.onClose(true, result.data);
                        },
                        (error) => {
                            showSnackBar(error.toString(), error?.response?.data?.errors[0], "error");
                        }
                    );
                }
            }
        }

        return (
            <div>
                <Modal open={open} onClose={() => closeModalCheck(true)}>
                    <div className={style.ModalMainNarrow}>
                        {!loading ? (
                            <div className={style.ModalBody}>
                                <Title pageTitle="Bank" />

                                <div className={style.ModalHeading}>
                                    <Typography variant={ tinyScreen ? "h6" : (smallScreen ? "h5" : "h4") }>Bank</Typography>
                                </div>

                                <div className={style.ModalContent}>
                                    <div className={style.ModalFields}>
                                        <TextField className={style.ModalContentFieldFullWidth}
                                            label="Bank Name"
                                            value = {bank?.bankName ?? ""}
                                            onChange={(e) => OnBankChanged("bankName", e.target.value.slice(0, 50))}
                                            size="small"
                                        />
                                       <TextField className={style.ModalContentFieldFullWidth}
                                            label="Bank Address"
                                            value = {bank?.bankAddress ?? ""}
                                            multiline
                                            rows={3}
                                            onChange={(e) => OnBankChanged("bankAddress", e.target.value.slice(0, 120))}
                                            size="small"
                                        />
                                       <TextField className={style.ModalContentFieldFullWidth}
                                            label="Phone"
                                            value = {bank?.phone ?? ""}
                                            onChange={(e) => OnBankChanged("phone", e.target.value.slice(0, 25))}
                                            size="small"
                                        />
                                       <TextField className={style.ModalContentFieldFullWidth}
                                            label="Fax"
                                            value = {bank?.fax ?? ""}
                                            onChange={(e) => OnBankChanged("fax", e.target.value.slice(0, 25))}
                                            size="small"
                                        />
                                        <TextField className={style.ModalContentFieldFullWidth}
                                            label="Region"
                                            value={bank?.region ?? getDefaultRegion()}
                                            onChange={(e) => OnBankChanged("region", e.target.value)}

                                            select
                                            size="small"
                                            fullWidth
                                        >
                                            {regionsWithBlank.map((item)=> (
                                                <MenuItem key={item.value} value={item.value}>
                                                    {item.name}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                    </div>
                                </div>

                                <div className={style.ModalFooter}>
                                    <Button
                                        variant="contained"
                                        color="secondary"
                                        className={style.margin}
                                        onClick={(e) => {
                                                SaveBank(false);
                                            }
                                        }
                                        size="small"
                                        style={{ padding: 1}}
                                    >
                                        Save
                                    </Button>
                                    <div style={{ width: `100%`, minWidth: "10" }}></div>
                                    <Button
                                        variant="contained"
                                        className={style.margin}
                                        onClick={(e) => closeModalCheck(false)}
                                        size="small"
                                        style={{ padding: 1}}
                                    >
                                        Close
                                    </Button>                                
                                </div>
                            </div>
                        ) : (
                            <div style={{ textAlign: "center" }}>
                                <CircularProgress color="primary" />
                            </div>
                        )}
                    </div>
                </Modal>

                <SnackBarMessage {...snackSettings} />

                <GeneralDialog
                    title={"Save Changes?"}
                    message={
                        "Changes have been made, did you want to save before closing?"
                    }
                    open={dialogOpen}
                    options={[
                        {
                        text: "Save",
                        action: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => SaveBank(true),
                        color: "secondary",
                        variant: "contained",
                        },
                        {
                        text: "No",
                        action: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => closeModal(),
                        color: "primary",
                        variant: "contained",
                        },
                    ]}
                />

            </div>
        );


    }

);


